import { AllContingencies } from "@amzn/hvh-simple-hire-checklist-internal-api-common";
import { UploadFileMeta } from "../../reduxStore/slices/docUploadSlice";
import {
  DOC_UPLOAD_STATUS_CARDS_NON_VALID_STATE,
  DOC_UPLOAD_STATUS_CARDS_VALID_STATE,
  EDM_DOC_TYPE,
  EDM_REVIEW_STATUS,
} from "../../utility/enums/common";
import { ActionPath, DocStatusCards } from "../../utility/types/common";
import { IPreassignedLinkResponse, PreassignedLinkFilesMutationType } from "./types";

interface IAdaptorParams {
  files: Record<string, UploadFileMeta | undefined>;
  preassignedLinkResponse: IPreassignedLinkResponse | undefined;
  status: EDM_REVIEW_STATUS;
  actionPath: ActionPath;
}

type ParsePresignedURLResponseType = (params: Pick<IAdaptorParams, "files">) => PreassignedLinkFilesMutationType;
type parseDocStatusCardsType = (params: IAdaptorParams) => { documents: DocStatusCards[] };
type setDocLoadingStateType = (params: Pick<IAdaptorParams, "files" | "actionPath">) => { documents: DocStatusCards[] };

export const parsePresignedURLResponse: ParsePresignedURLResponseType = ({ files }) => {
  const arrayOfFiles: PreassignedLinkFilesMutationType = [];
  Object.keys(files).forEach(function (key) {
    const file = files[key];
    if (file) {
      arrayOfFiles.push({ extension: file?.type, filename: file?.name, sha256: file?.sha256 });
    }
  });

  return arrayOfFiles;
};

export const parseDocStatusCards: parseDocStatusCardsType = ({
  files,
  preassignedLinkResponse,
  status,
  actionPath,
}) => {
  const arrayOfWidgets: DocStatusCards[] = [];
  Object.keys(files).forEach(function (key) {
    const file = files[key];
    if (file) {
      arrayOfWidgets.push({
        state:
          status !== EDM_REVIEW_STATUS.ERROR
            ? DOC_UPLOAD_STATUS_CARDS_VALID_STATE.SUCCESSFUL
            : DOC_UPLOAD_STATUS_CARDS_NON_VALID_STATE.FAILED,
        documentId: file.id,
        documentTypeId: file.documentTypeId as EDM_DOC_TYPE,
        draftId: preassignedLinkResponse?.draft_id || "",
        fileName: file.name,
        contentType: file.type,
        contentSize: file.size,
        sha256: file.sha256,
        actionPath,
      });
    }
  });

  return { documents: [...arrayOfWidgets] };
};

export const setDocLoadingState: setDocLoadingStateType = ({ files, actionPath }) => {
  const arrayOfWidgets: DocStatusCards[] = [];
  Object.keys(files).forEach(function (key) {
    const file = files[key];
    if (file) {
      arrayOfWidgets.push({
        state: DOC_UPLOAD_STATUS_CARDS_VALID_STATE.PROCESSING,
        documentId: file.id,
        documentTypeId: file.documentTypeId as EDM_DOC_TYPE,
        draftId: key,
        fileName: file.name,
        contentType: file.type,
        contentSize: file.size,
        sha256: file.sha256,
        actionPath,
      });
    }
  });

  return { documents: [...arrayOfWidgets] };
};

export const getDocSizeWithMetric = (size: string): string => {
  return String(Number(size) / 1_000_000).slice(0, 3) + "MB";
};

export const _arrayBufferToBase64 = (buffer: ArrayBuffer) => {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

export const _fileToSHA256 = async (file: File) => {
  const fileArrayBuffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", fileArrayBuffer);
  const base64 = _arrayBufferToBase64(digest);
  return base64;
};

export const getSha256FromBase64 = (fileParam: any) => {
  const file = new File([fileParam.name], fileParam.name, {
    type: fileParam.type,
  });

  return _fileToSHA256(file);
};

export const mapAllContigenciesStatuses = (url: string, allContingenciesResponse: AllContingencies) => {
  if (url.startsWith("/api/status/right-to-work/candidate/")) {
    return allContingenciesResponse.RightToWork;
  }
  if (url.startsWith("/api/status/background-check/candidate/")) {
    return allContingenciesResponse.BGC;
  }
  if (url.startsWith("/api/caas/")) {
    return allContingenciesResponse.WorkAuthorization;
  }
  if (url.startsWith("/api/status/i9/candidate/")) {
    return allContingenciesResponse.WorkAuthorization;
  }
};
